import { ShieldCheck } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { PortalFooter } from "../components/PortalFooter";
import { appConfig } from "../config/appConfig";
import { sendPasswordReset, sendSignInLink } from "../lib/auth";

type LoginPageProps = {
  error: string | null;
  initialFlow?: "signIn" | "magicLink" | "forgotPassword";
  onSignIn: (email: string, password: string) => Promise<void>;
  onDemoSignIn: () => Promise<void>;
  onRegister: () => void;
  onClearError?: () => void;
};

export function LoginPage({ error, initialFlow = "signIn", onSignIn, onDemoSignIn, onRegister, onClearError }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flow, setFlow] = useState<"signIn" | "magicLink" | "forgotPassword">(initialFlow);
  const [flowStatus, setFlowStatus] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const authEmailInFlightRef = useRef(false);
  const [sentAuthEmailKey, setSentAuthEmailKey] = useState<string | null>(null);

  useEffect(() => {
    onClearError?.();
  }, []);

  useEffect(() => {
    setFlow(initialFlow);
    setFlowStatus(null);
    setFlowError(null);
    onClearError?.();
  }, [initialFlow]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSignIn(email, password);
    } finally {
      setIsSubmitting(false);
    }
  }

  function openFlow(nextFlow: typeof flow) {
    setFlow(nextFlow);
    setFlowStatus(null);
    setFlowError(null);
    setSentAuthEmailKey(null);
    onClearError?.();
  }

  async function handleMagicLinkSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    const requestKey = `magicLink:${normalizedEmail}`;
    if (authEmailInFlightRef.current || sentAuthEmailKey === requestKey) return;
    authEmailInFlightRef.current = true;
    setIsSubmitting(true);
    setFlowStatus(null);
    setFlowError(null);
    try {
      await sendSignInLink(email);
      setSentAuthEmailKey(requestKey);
      setFlowStatus("Check your email for a secure sign-in link.");
    } catch (error) {
      setSentAuthEmailKey(null);
      setFlowError(authEmailErrorMessage(error, "signInLink"));
    } finally {
      authEmailInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  async function handleForgotPasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    const requestKey = `passwordReset:${normalizedEmail}`;
    if (authEmailInFlightRef.current || sentAuthEmailKey === requestKey) return;
    authEmailInFlightRef.current = true;
    setIsSubmitting(true);
    setFlowStatus(null);
    setFlowError(null);
    try {
      await sendPasswordReset(email);
      setSentAuthEmailKey(requestKey);
      setFlowStatus("Check your email for password reset instructions.");
    } catch (error) {
      setSentAuthEmailKey(null);
      setFlowError(authEmailErrorMessage(error, "passwordReset"));
    } finally {
      authEmailInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }

  function handleAuthEmailChange(nextEmail: string) {
    setEmail(nextEmail);
    setFlowStatus(null);
    setFlowError(null);
    setSentAuthEmailKey(null);
  }

  return (
    <main className="login-page">
      {appConfig.showTestBanner ? <div className="test-banner">TEST ENVIRONMENT</div> : null}
      <section className="login-brand" aria-label="Companion Pharmacy Prescriber Portal">
        <img
          src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
          alt="Companion Pharmacy"
        />
        <h1>Prescriber Portal</h1>
        <p>Submit and manage prescriptions with a lighter, cleaner workflow.</p>
      </section>

      <section className="login-panel" aria-labelledby="login-heading">
        <ShieldCheck className="login-mark" aria-hidden="true" size={32} />
        <h2 id="login-heading">Welcome back</h2>
        <p>{appConfig.copy.loginSubtitle}</p>

        {flow === "signIn" ? (
          <>
            <form onSubmit={handleSubmit}>
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  onClearError?.();
                }}
                required
              />

              <div className="label-row">
                <label htmlFor="password">Password</label>
                <button className="inline-link-button" type="button" onClick={() => openFlow("forgotPassword")}>
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  onClearError?.();
                }}
                required
              />

              {error ? <div className="form-alert">{error}</div> : null}

              <button className="primary-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <button className="link-button magic-link-button" type="button" onClick={() => openFlow("magicLink")}>
              Email me a sign-in link
            </button>
          </>
        ) : null}

        {flow === "magicLink" ? (
          <AuthEmailFlow
            buttonText="Send sign-in link"
            email={email}
            heading="Email me a sign-in link"
            isSubmitting={isSubmitting}
            status={flowStatus}
            error={flowError}
            onBack={() => openFlow("signIn")}
            onEmailChange={handleAuthEmailChange}
            onSubmit={handleMagicLinkSubmit}
          />
        ) : null}

        {flow === "forgotPassword" ? (
          <AuthEmailFlow
            buttonText="Send reset instructions"
            email={email}
            heading="Reset your password"
            isSubmitting={isSubmitting}
            status={flowStatus}
            error={flowError}
            onBack={() => openFlow("signIn")}
            onEmailChange={handleAuthEmailChange}
            onSubmit={handleForgotPasswordSubmit}
          />
        ) : null}

        {appConfig.environment === "local" ? (
          <button className="secondary-button login-demo-button" type="button" onClick={onDemoSignIn}>
            Review local draft
          </button>
        ) : null}

        <footer className="login-panel-footer">
          <span>First time here?</span>
          <button className="inline-link-button" type="button" onClick={onRegister}>
            Request access
          </button>
        </footer>
      </section>

      <PortalFooter />
    </main>
  );
}

function authEmailErrorMessage(error: unknown, flow: "signInLink" | "passwordReset"): string {
  const status = typeof error === "object" && error !== null && "status" in error ? Number(error.status) : null;
  const code = typeof error === "object" && error !== null && "code" in error ? String(error.code) : "";
  const message = error instanceof Error ? error.message : String(error || "");
  const diagnosticText = `${code} ${message}`.toLowerCase();

  if (status === 429 || diagnosticText.includes("rate") || diagnosticText.includes("security purposes")) {
    return flow === "passwordReset"
      ? "Too many password reset attempts. Please wait a few minutes, then try again."
      : "Too many sign-in link attempts. Please wait a few minutes, then try again.";
  }

  if (status === 422 || diagnosticText.includes("valid email") || diagnosticText.includes("invalid email")) {
    return flow === "passwordReset"
      ? "To reset your password, please enter a valid email address."
      : "To send a sign-in link, please enter a valid email address.";
  }

  return flow === "passwordReset"
    ? "Password reset is temporarily unavailable. Please try again shortly."
    : "Sign-in links are temporarily unavailable. Please try again shortly.";
}

function AuthEmailFlow({
  buttonText,
  email,
  heading,
  isSubmitting,
  status,
  error,
  onBack,
  onEmailChange,
  onSubmit
}: {
  buttonText: string;
  email: string;
  heading: string;
  isSubmitting: boolean;
  status: string | null;
  error: string | null;
  onBack: () => void;
  onEmailChange: (email: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form className="auth-flow-form" onSubmit={onSubmit}>
      <h3>{heading}</h3>
      <label>
        Email address
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          required
        />
      </label>
      {status ? <div className="form-success">{status}</div> : null}
      {error ? <div className="form-alert">{error}</div> : null}
      <div className="button-row">
        <button className="primary-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : buttonText}
        </button>
        <button className="secondary-button" type="button" onClick={onBack}>
          Back
        </button>
      </div>
    </form>
  );
}
